import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSettings, upsertSetting } from '../../api';
import { PageHeader, Table, EmptyState, TableSkeleton } from '../../components/ui';
import { Settings } from 'lucide-react';
import toast from 'react-hot-toast';

export default function SettingsPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ key: '', value: '', description: '', isPublic: false });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => getSettings().then((r) => r.data.data.settings || r.data.data || []),
  });
  const settings = Array.isArray(data) ? data : [];

  const save = useMutation({
    mutationFn: () => {
      let value: any = form.value;
      try { value = JSON.parse(form.value); } catch { /* keep string */ }
      return upsertSetting({ key: form.key.trim(), value, description: form.description, isPublic: form.isPublic });
    },
    onSuccess: () => {
      toast.success('Setting saved');
      setForm({ key: '', value: '', description: '', isPublic: false });
      qc.invalidateQueries({ queryKey: ['admin-settings'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const edit = (s: any) => {
    setForm({
      key: s.key,
      value: typeof s.value === 'string' ? s.value : JSON.stringify(s.value),
      description: s.description || '',
      isPublic: !!s.isPublic,
    });
  };

  return (
    <div className="p-6">
      <PageHeader title="App settings" subtitle="Platform configuration keys used by the mobile app" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card p-5">
          <h3 className="font-semibold mb-3">Add / update key</h3>
          <input className="input mb-3" placeholder="key (e.g. support.email)" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} />
          <textarea className="input min-h-[80px] mb-3" placeholder='value (text or JSON)' value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
          <input className="input mb-3" placeholder="description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <label className="flex items-center gap-2 text-sm mb-3">
            <input type="checkbox" checked={form.isPublic} onChange={(e) => setForm({ ...form, isPublic: e.target.checked })} />
            Public (exposed on /app-settings)
          </label>
          <button disabled={!form.key.trim() || save.isPending} onClick={() => save.mutate()} className="btn-primary w-full">Save setting</button>
        </div>
        <div className="card overflow-hidden lg:col-span-2">
          {isLoading ? <div className="p-5"><TableSkeleton rows={8} cols={4} /></div>
            : settings.length === 0 ? <EmptyState message="No settings yet" icon={<Settings size={32} />} />
            : (
              <Table>
                <thead className="bg-gray-50 border-b"><tr>
                  <th className="table-th">Key</th><th className="table-th">Value</th><th className="table-th">Public</th><th className="table-th"></th>
                </tr></thead>
                <tbody>
                  {settings.map((s: any) => (
                    <tr key={s._id || s.key} className="table-row">
                      <td className="table-td text-sm font-medium">{s.key}</td>
                      <td className="table-td text-xs max-w-[280px] truncate">{typeof s.value === 'object' ? JSON.stringify(s.value) : String(s.value)}</td>
                      <td className="table-td text-xs">{s.isPublic ? 'Yes' : 'No'}</td>
                      <td className="table-td"><button className="text-xs text-primary-600" onClick={() => edit(s)}>Edit</button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
        </div>
      </div>
    </div>
  );
}
